import { AlertSoundOutcome, AlertSoundSpec } from './alert-sound.type';

/** Pico de cada nota. Baixo de propósito: notas se sobrepõem (arpejo, parciais) e a soma não pode estourar. */
const PEAK_GAIN = 0.2;

/** Ataque curto e linear: corte seco em senoide estala no alto-falante barato de tablet. */
const ATTACK_SECONDS = 0.012;

/** `exponentialRamp` não aceita zero: o "silêncio" do decaimento é este valor. */
const SILENCE = 0.0001;

type AudioContextConstructor = new () => AudioContext;

function resolveAudioContext(view: Window): AudioContextConstructor | undefined {
  const candidate = view as { AudioContext?: AudioContextConstructor; webkitAudioContext?: AudioContextConstructor };
  return candidate.AudioContext ?? candidate.webkitAudioContext;
}

/** Um contexto por tela: criar um por som estoura o limite do navegador depois de algumas dezenas de avisos. */
export function createAlertAudioContext(view: Window): AudioContext | undefined {
  const AudioContextCtor = resolveAudioContext(view);
  return AudioContextCtor ? new AudioContextCtor() : undefined;
}

/**
 * Toca um som de aviso (M2-D69). `blocked` quando o navegador segura o áudio até o primeiro toque do usuário
 * na página (política de autoplay) — quem chama avisa a tela para pedir esse toque.
 */
export async function playAlertSound(context: AudioContext, spec: AlertSoundSpec): Promise<AlertSoundOutcome> {
  try {
    if (context.state === 'suspended') {
      await context.resume();
    }
    if (context.state !== 'running') {
      return 'blocked';
    }

    const origin = context.currentTime;

    for (const tone of spec.tones) {
      const components: readonly (readonly [number, number])[] = [[1, 1], ...(tone.partials ?? [])];

      for (const [multiplier, relativeVolume] of components) {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const startAt = origin + tone.startAt;
        const endAt = startAt + tone.duration;
        const peak = PEAK_GAIN * (tone.volume ?? 1) * relativeVolume;

        oscillator.type = tone.wave ?? 'sine';
        oscillator.frequency.value = tone.frequency * multiplier;

        gain.gain.setValueAtTime(SILENCE, startAt);
        gain.gain.linearRampToValueAtTime(peak, startAt + ATTACK_SECONDS);
        gain.gain.exponentialRampToValueAtTime(SILENCE, endAt);

        oscillator.connect(gain).connect(context.destination);
        oscillator.start(startAt);
        oscillator.stop(endAt + 0.05);
      }
    }

    return 'played';
  } catch {
    return 'blocked';
  }
}
